// OrgChart — Admin > People > Org Chart.
//
// Five interchangeable visualisations of the same /api/admin/org-chart
// payload. The container fetches once, then hands the tree to the
// view selected via the top-row toggle:
//
//   • Outline  — collapsible text tree (the long-standing default)
//   • Tree     — top-down boxes joined by orthogonal connectors
//   • Radial   — sunburst rings, weighted by subtree leaf count
//   • Treemap  — nested rectangles, area ∝ team size
//   • Network  — radial cluster ("spider") of bubbles + edges
//
// All five share `shared.js` (role colours, name resolution, search
// semantics) so behaviour stays consistent when switching views.
import React, { useEffect, useMemo, useState } from "react";
import {
  AlignLeft, GitBranch, Disc, LayoutGrid, Share2, Search, Users,
} from "lucide-react";

import OutlineView from "./orgchart/OutlineView";
import TreeView    from "./orgchart/TreeView";
import RadialView  from "./orgchart/RadialView";
import TreemapView from "./orgchart/TreemapView";
import NetworkView from "./orgchart/NetworkView";
import { childrenOf } from "./orgchart/shared";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const VIEWS = [
  { key: "outline", label: "Outline",  Icon: AlignLeft,  Component: OutlineView,  hint: "Collapsible text tree (default)" },
  { key: "tree",    label: "Tree",     Icon: GitBranch,  Component: TreeView,     hint: "Top-down boxes with connectors" },
  { key: "radial",  label: "Radial",   Icon: Disc,       Component: RadialView,   hint: "Sunburst rings, area ∝ team size" },
  { key: "treemap", label: "Treemap",  Icon: LayoutGrid, Component: TreemapView,  hint: "Nested rectangles, area ∝ headcount" },
  { key: "network", label: "Network",  Icon: Share2,     Component: NetworkView,  hint: "Radial cluster of bubbles + edges" },
];

const VIEW_KEYS    = VIEWS.map((v) => v.key);
const STORAGE_KEY  = "kwery_orgchart_view";
const DEFAULT_VIEW = "outline";

export default function OrgChart({ onSelectUser }) {
  const [tree,    setTree]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [stats,   setStats]   = useState(null);
  const [view,    setView]    = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return VIEW_KEYS.includes(stored) ? stored : DEFAULT_VIEW;
    } catch { return DEFAULT_VIEW; }
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/org-chart`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        const nodes = Array.isArray(d) ? d : d?.tree || [];
        setTree(nodes);
        setStats(d?.stats || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the last-used view so an admin doesn't have to re-select it
  // every visit.
  const pickView = (key) => {
    setView(key);
    try { localStorage.setItem(STORAGE_KEY, key); } catch { /* private mode etc. */ }
  };

  // Tree count fallback when /org-chart didn't return a stats block.
  const flatCount = useMemo(() => {
    let n = 0;
    const count = (nodes) => nodes.forEach((node) => { n += 1; count(childrenOf(node)); });
    count(tree);
    return n;
  }, [tree]);

  const ActiveView = VIEWS.find((v) => v.key === view)?.Component || OutlineView;
  const activeMeta = VIEWS.find((v) => v.key === view);

  return (
    <div className="space-y-4">

      {/* Stats bar — unchanged from the single-view era */}
      {(stats || flatCount > 0) && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Total Headcount", value: stats?.total || flatCount, color: "#00ff90" },
            { label: "Departments",     value: stats?.departments || "—",  color: "#60a5fa" },
            { label: "Managers",        value: stats?.managers    || "—",  color: "#c084fc" },
          ].map(({ label, value, color }) => (
            <div key={label} className={`${card} p-4 text-center`}>
              <p className="text-xl font-bold" style={{ color }}>{value}</p>
              <p className="text-xs text-white/30 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Chart card */}
      <div className={card}>

        {/* Header — title + view toggle + search */}
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3 flex-wrap">
          <Users size={14} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            Organisation Chart
          </span>

          {/* View toggle */}
          <div className="flex items-center gap-1 ml-2 bg-black/40 border border-white/5 rounded-lg p-0.5">
            {VIEWS.map(({ key, label, Icon, hint }) => {
              const active = view === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickView(key)}
                  title={hint}
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                    active
                      ? "bg-[#00ff90]/15 text-[#00ff90]"
                      : "text-white/40 hover:text-white/70 hover:bg-white/5"
                  }`}
                >
                  <Icon size={12} />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Search size={13} className="text-white/30" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, dept…"
              className="bg-black border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-[#00ff90]/30 transition-colors w-48"
            />
          </div>
        </div>

        {/* Body */}
        {loading && (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 rounded-lg bg-white/5 animate-pulse" style={{ marginLeft: `${(i % 3) * 24}px` }} />
            ))}
          </div>
        )}
        {error && <p className="p-4 text-red-400 text-sm">⚠ {error}</p>}
        {!loading && !error && tree.length === 0 && (
          <p className="p-8 text-white/20 text-sm italic text-center">No org chart data available.</p>
        )}
        {!loading && !error && tree.length > 0 && (
          <ActiveView tree={tree} search={search} onSelectUser={onSelectUser} />
        )}

        {/* Footer — current-view caption + click-hint */}
        <div className="px-5 py-3 border-t border-white/5 flex items-center gap-3 flex-wrap">
          <span className="text-xs text-white/30">
            {activeMeta?.label}
            {activeMeta?.hint && <span className="text-white/20"> — {activeMeta.hint}</span>}
          </span>
          <span className="text-xs text-white/20 ml-auto">
            Click any person to open their profile drawer
          </span>
        </div>
      </div>
    </div>
  );
}
